import { useCallback, useState } from 'react';
import { useQuery } from '@apollo/client';

import { GET_ACTIVE_SCREEN_POP } from './quote-forms.queries';

// Per tab: a dismissed call stays dismissed if the agent reloads or opens another New Offer in
// this tab, but a fresh tab for the same call gets the prefill again.
const DISMISSED_CALL_ID_KEY = 'dismissedScreenPopCallId';

const readDismissedCallId = () => {
  try {
    return window.sessionStorage.getItem(DISMISSED_CALL_ID_KEY);
  } catch {
    return null;
  }
};

/**
 * The agent's active Dialpad campaign screenpop, for quote tabs that did not come from the pop
 * itself (New Offer header, an existing tab). `enabled` is false whenever the form already has a
 * source of truth for the affinity: a URL param, an offer being re-quoted, or an agency user whose
 * affinity is their own.
 */
export const useActiveScreenPop = ({ enabled }) => {
  const [dismissedCallId, setDismissedCallId] = useState(readDismissedCallId);
  const { data, loading } = useQuery(GET_ACTIVE_SCREEN_POP, { skip: !enabled, fetchPolicy: 'no-cache' });

  const screenPop = data?.getActiveScreenPop;
  const activeScreenPop = enabled && screenPop && screenPop.callId !== dismissedCallId ? screenPop : null;

  const dismiss = useCallback(() => {
    if (!screenPop) {
      return;
    }
    try {
      window.sessionStorage.setItem(DISMISSED_CALL_ID_KEY, screenPop.callId);
    } catch {
      // storage unavailable: the in-memory state still hides it for this page
    }
    setDismissedCallId(screenPop.callId);
  }, [screenPop]);

  return { activeScreenPop, loading: enabled && loading, dismiss };
};
